import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { housecallProAPI } from '../lib/housecallproApi';
import { toast } from 'sonner';

export function HcpSyncButton() {
  const [syncing, setSyncing] = useState(false);
  const [connected, setConnected] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    housecallProAPI.getStatus()
      .then(res => setConnected(res.data?.configured && res.data?.connection_status === 'connected'))
      .catch(() => setConnected(false));
  }, []);

  const handleSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    setProgress(null);
    try {
      const res = await housecallProAPI.runImport({
        domains: ['customers', 'locations', 'jobs', 'materials'],
      });
      const taskId = res.data?.task_id;
      if (!taskId) { toast.error('Sync failed to start'); setSyncing(false); return; }

      if (res.data?.status === 'already_running') {
        toast.info('HCP sync already running...');
      } else {
        toast.info('HCP sync started...');
      }

      const poll = async () => {
        try {
          const s = await housecallProAPI.getImportStatus(taskId);
          const d = s.data;
          setProgress(d);
          if (d.status === 'completed') {
            toast.success('HCP sync complete');
            setSyncing(false);
            setProgress(null);
            return;
          }
          if (d.status === 'failed') {
            toast.error(`HCP sync failed: ${d.error || 'Unknown'}`);
            setSyncing(false);
            setProgress(null);
            return;
          }
          setTimeout(poll, 2000);
        } catch {
          setSyncing(false);
          setProgress(null);
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
      setSyncing(false);
    }
  }, [syncing]);

  if (connected === null || connected === false) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative text-white hover:bg-white/10 hover:text-white"
            onClick={handleSync}
            disabled={syncing}
            data-testid="hcp-sync-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing && progress?.current_domain && (
              <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[200px]">
          {syncing
            ? progress?.current_domain
              ? `Syncing ${progress.current_domain}... (${progress.progress})`
              : 'HCP sync running...'
            : 'Sync Housecall Pro'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
